import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { InboxEntry } from '../../../models/inbox-entry.model';
import { inboxTargetUrl, testlaufZiel } from '../../../models/inbox-target';
import { InboxService } from '../../../services/inbox.service';
import { HeaderInboxBadgeService } from '../../../services/header-inbox-badge.service';
import { TestlaufOeffnenService } from '../../../services/testlauf-oeffnen.service';
import { artInfo, artVon, AufgabenArtInfo, Faelligkeit, faelligkeit } from '../aufgaben-art';
import { AufgabenZeitpunkt, aufgabenZeitpunkt } from '../aufgaben-zeitpunkt';
import { HEUTE, MeineAufgabenStore } from '../meine-aufgaben.store';

interface AufgabenZeile {
  eintrag: InboxEntry;
  info: AufgabenArtInfo;
  stufe: Faelligkeit | null;
  zeit: AufgabenZeitpunkt;
}

@Component({
  selector: 'app-aufgaben-liste',
  imports: [MatIcon, TranslatePipe],
  templateUrl: './aufgaben-liste.component.html',
  styleUrl: './aufgaben-liste.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AufgabenListeComponent {
  protected readonly store = inject(MeineAufgabenStore);
  private readonly inboxService = inject(InboxService);
  private readonly inboxBadge = inject(HeaderInboxBadgeService);
  private readonly testlaufOeffnen = inject(TestlaufOeffnenService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly jetzt = inject(HEUTE);

  // Als Ausdruck, weil der Formatierer Leerzeichen am Rand von Template-Text entfernt.
  protected readonly trenner = ' · ';
  protected readonly oeffnet = signal<string | null>(null);

  protected readonly kopfKey = computed(() => {
    const filter = this.store.filter();
    return filter ? artInfo(filter).labelKey : 'home.aufgaben.liste.titel';
  });

  protected readonly zeilen = computed<AufgabenZeile[]>(() => {
    const jetzt = this.jetzt();
    return this.store.liste().map((eintrag) => ({
      eintrag,
      info: artInfo(artVon(eintrag)!),
      stufe: faelligkeit(eintrag.dueAt ?? null, jetzt),
      zeit: aufgabenZeitpunkt(eintrag.createdAt, jetzt),
    }));
  });

  protected ausfuehren(eintrag: InboxEntry): void {
    if (eintrag.unread) {
      this.inboxService
        .markRead(eintrag.id)
        .subscribe({ next: () => this.inboxBadge.refresh(), error: () => undefined });
    }
    const ziel = testlaufZiel(eintrag);
    if (ziel) {
      this.oeffnet.set(eintrag.id);
      this.testlaufOeffnen
        .oeffnen(ziel.projectId, ziel.runId)
        .pipe(finalize(() => this.oeffnet.set(null)))
        .subscribe();
      return;
    }
    if (!eintrag.page) {
      this.fehler('postfach.navigate.missingTarget');
      return;
    }
    this.router.navigateByUrl(inboxTargetUrl(eintrag)).then((ok) => {
      if (!ok) this.fehler('postfach.navigate.error');
    });
  }

  private fehler(key: string): void {
    this.snackBar.open(this.translate.instant(key), undefined, { duration: 4000, panelClass: 'snackbar-error' });
  }
}
